import { Dictionary } from 'lodash';
import { castArray, compact, filter, find, flatMap, forEach, groupBy, keyBy, map, some } from 'lodash/fp';
import MediumUser from '../../../../types/MediumUser';
import MiniUser from '../../../../types/MiniUser';
import { CurrentUserDto, UserProfileDto } from '../../../../types/UserDtos';

type User = MiniUser | UserProfileDto | CurrentUserDto;
type UserOneOrMore = User[] | User;

type Tree = {
    parent?: Tree;
    children: Tree[];
    user: MediumUser;
};

function managersOf(users: MediumUser[], usersToExpand: UserOneOrMore) {
    usersToExpand = castArray(usersToExpand);
    const forOneUser = (user: User) => findUser(users, user.managerId);
    return compact(map(forOneUser, usersToExpand));
}

function managementChainOfId(users: MediumUser[], userId: string) {
    const user = findUser(users, userId);
    return user ? managementChainOf(users, user) : [];
}

function managementChainOf(users: MediumUser[], user: User[]): MediumUser[][];
function managementChainOf(users: MediumUser[], user: User): MediumUser[];
function managementChainOf(users: MediumUser[], user: UserOneOrMore) {
    const forOneUser = (u: User) => {
        const manager = findUser(users, u.managerId);
        if (manager) {
            return [manager].concat(managementChainOf(users, manager));
        } else {
            return [];
        }
    };

    if (Array.isArray(user)) {
        return map(forOneUser, user);
    } else {
        return forOneUser(user);
    }
}

function findUser(users: MediumUser[], id: string | null) {
    return find(u => u.id === id, users);
}

function directReportsOf(users: MediumUser[], usersToExpand: UserOneOrMore) {
    usersToExpand = castArray(usersToExpand);
    const forOneUser = (manager: User) => filter(({ managerId }) => managerId === manager.id, users);
    return flatMap(forOneUser, usersToExpand);
}

function indirectReportsOf(users: MediumUser[], usersToExpand: UserOneOrMore) {
    usersToExpand = castArray(usersToExpand);
    const usersByManagerId = groupBy('managerId', users);
    const trees = map(buildSubtree.bind(null, users, usersByManagerId, undefined), usersToExpand);
    return map(
        (tree: Tree) => tree.user,
        flatMap(
            flattenTree,
            flatMap((tree: Tree) => tree.children, trees)
        )
    );
}

function buildSubtree(
    users: MediumUser[],
    usersByManagerId: Dictionary<MediumUser[]>,
    maxLevel: number | undefined,
    initialUser: User
) {
    if (maxLevel !== undefined) {
        maxLevel -= 1;
    }

    const user = users.find(u => u.id === initialUser.id)!;

    if (maxLevel !== undefined && maxLevel <= 0) {
        return { user, children: [] };
    } else {
        const children = map(buildSubtree.bind(null, users, usersByManagerId, maxLevel), usersByManagerId[user.id]);
        const parent: Tree = { user, children: [] };
        parent.children = map(child => ({ ...child, parent }), children);
        return parent;
    }
}

function buildTree(users: MediumUser[], rootManager: User, maxLevel?: number) {
    const usersByManagerId = groupBy('managerId', users);
    return buildSubtree(users, usersByManagerId, maxLevel, rootManager);
}

function flattenTree(manager: Tree): Tree[] {
    return [manager].concat(flatMap(flattenTree, manager.children));
}

function buildTreesFromUsers(users: MediumUser[]) {
    const userExists = (id: string | null) => users.some(user => user.id === id);
    const rootUsers = filter(user => !userExists(user.managerId), users);
    return map(user => buildTree(users, user), rootUsers);
}

function sortTreesByUser(compare: (a: MediumUser, b: MediumUser) => number, trees: Tree[]) {
    trees.sort((a: Tree, b: Tree) => compare(a.user, b.user));
    forEach(tree => sortTreesByUser(compare, tree.children), trees);
}

function buildTeamsFromUsers(users: MediumUser[]): Tree[] {
    const isManager = (user: MediumUser) => some({ managerId: user.id }, users);

    const managers = filter(user => isManager(user), users);
    return flatMap(manager => {
        const children = filter(user => user.managerId === manager.id, users);
        const childrenObjects = map(child => ({ user: child, children: [] }), children);
        return [{ user: manager, children: childrenObjects }];
    }, managers);
}

function groupManagersAndNonManagers(users: MediumUser[]) {
    const userMap = keyBy(user => user.id, users);
    const nonManagers = new Set(users);
    const managers = new Set<MediumUser>();

    users.forEach(user => {
        if (!user.managerId) {
            return;
        }

        const manager = userMap[user.managerId];

        if (!manager) {
            return;
        }

        nonManagers.delete(manager);
        managers.add(manager);
    });

    return {
        managers: [...managers],
        nonManagers: [...nonManagers],
    };
}

export {
    buildTeamsFromUsers,
    buildTree,
    buildTreesFromUsers,
    directReportsOf,
    flattenTree,
    groupManagersAndNonManagers,
    indirectReportsOf,
    managementChainOf,
    managementChainOfId,
    managersOf,
    sortTreesByUser,
};
